import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  TextField,
  Stack,
  InputAdornment,
  Box,
  Typography,
  Paper,
  Fade,
  CircularProgress,
  useTheme,
  Divider,
  Card,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useQuery } from "@apollo/client";
import AddIngredients from "../inventory/AddIngredients";
import { useNotification } from "../../Context/notificationContext";
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { GET_INGREDIENTS } from "./ChooseGrainsForm";

export default function ChooseSpicesForm({ task, onSubmit }) {
  const theme = useTheme();
  
  const [selectedItem, setSelectedItem] = useState(task.item_name || "");
  const [quantity, setQuantity] = useState(task.quantity || "");
  const [error, setError] = useState("");
  const [itemError, setItemError] = useState("");
  const { showNotification } = useNotification();
  const minQuantity = 1;
  const maxQuantity = 100;

  const {
    data,
    loading: getIngredientsLoading,
    error: getIngredientsError,
    refetch: refetchIngredients,
  } = useQuery(GET_INGREDIENTS, {
    variables: {
      ingredientType: "SPICE",
    },
    fetchPolicy: "cache-and-network",
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validateForm()) {
      task.args = selectedItem + " - " + quantity + "g";
      task.quantity = quantity;
      task.item_name = selectedItem;
      onSubmit(task);
    }
  };

  const validateForm = () => {
    if (!selectedItem) {
      setItemError("Please select a spice");
      setError("");
      return false;
    }
    if (+quantity < minQuantity || +quantity > maxQuantity) {
      setError(
        `Quantity must be between ${minQuantity} and ${maxQuantity} grams`
      );
      setItemError("");
      return false;
    }
    setError("");
    setItemError("");
    return true;
  };

  const [addIngredientActive, setAddIngredientActive] = useState(false);
  const [item_name, setItem_name] = useState("");
  const [ingredientType, setIngredientType] = useState(null);
  const [ingredientImage, setIngredientImage] = useState(null);
  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    if (data && data.getIngredients) {
      setIngredients(data.getIngredients);
    }

    if (getIngredientsError) {
      showNotification("error", getIngredientsError.message);
    }
  }, [data, getIngredientsError, showNotification]);

  return addIngredientActive ? (
    // Add Ingredient
    <Fade in={addIngredientActive}>
      <Box>
        <AddIngredients
          newIngredientName={item_name}
          setNewIngredientName={setItem_name}
          ingredientType={ingredientType}
          setIngredientType={setIngredientType}
          ingredientImage={ingredientImage}
          setIngredientImage={setIngredientImage}
          ingredients={ingredients}
          setIngredients={setIngredients}
          setAddIngredientActive={setAddIngredientActive}
          refetchIngredients={refetchIngredients}
        />
      </Box>
    </Fade>
  ) : (
    <Fade in={!addIngredientActive}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          transition: 'all 0.3s ease'
        }}
      >
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            mb: 3, 
            color: theme.palette.primary.main,
            fontWeight: 600,
            textAlign: 'center'
          }}
        >
          Select Spice & Quantity
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Spice Selection */}
            <Box>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 0.5, 
                  color: theme.palette.text.secondary,
                  fontWeight: 500
                }}
              >
                Spice Selection
              </Typography>
              <Autocomplete
                options={data?.getIngredients || []}
                getOptionLabel={(option) => option.ingredient_name || ""}
                loading={getIngredientsLoading}
                loadingText="Fetching spices..."
                filterOptions={(options, { inputValue }) =>
                  options.filter((option) =>
                    option.ingredient_name.toLowerCase().includes(inputValue.toLowerCase())
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search spice by name"
                    variant="outlined"
                    value={selectedItem}
                    error={!!itemError}
                    helperText={itemError}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {getIngredientsLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                      sx: { borderRadius: 2 }
                    }}
                    fullWidth
                  />
                )}
                renderOption={(props, option) => (
                  <Box
                    component={Card}
                    {...props}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      my: 0.5,
                      p: 1,
                      borderRadius: 1.5,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        boxShadow: 1
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Box
                      sx={{
                        width: 45,
                        height: 45,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: theme.palette.background.default
                      }}
                    >
                      <img
                        src={option.image_url || "/placeholder-16x9.png"}
                        alt={option.ingredient_name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          e.target.src = "/placeholder-16x9.png";
                        }}
                      />
                    </Box>
                    <Typography variant="body1">{option.ingredient_name}</Typography>
                  </Box>
                )}
                noOptionsText={
                  getIngredientsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography>Loading spices...</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ p: 1 }}>
                      <Typography sx={{ mb: 1 }}>Spice not found in inventory</Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setAddIngredientActive(true)}
                        fullWidth
                        sx={{ 
                          mt: 1, 
                          textTransform: 'none',
                          borderRadius: 1.5,
                          py: 1
                        }}
                      >
                        Add New Spice
                      </Button>
                    </Box>
                  )
                }
                onInputChange={(event, newInputValue) => setSelectedItem(newInputValue)}
                value={
                  ingredients.find((item) => item.ingredient_name === selectedItem) || null
                }
                fullWidth
                disablePortal
              />
            </Box>
            
            {/* Quantity Input */}
            <Box>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 0.5, 
                  color: theme.palette.text.secondary,
                  fontWeight: 500
                }}
              >
                Quantity
              </Typography>
              <FormControl fullWidth variant="outlined">
                <TextField
                  placeholder="Enter amount"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  error={!!error}
                  helperText={error || `Allowed range: ${minQuantity}-${maxQuantity} grams`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary">grams</Typography>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 }
                  }}
                />
              </FormControl>
            </Box>
            
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              endIcon={<ArrowForwardIcon />}
              sx={{
                py: 1.5,
                mt: 2,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                }
              }}
            >
              Submit Selection
            </Button>
          </Stack>
        </form>
      </Paper>
    </Fade>
  );
}
